"use client";
import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { cx } from "class-variance-authority";
import {
  SelectAutoComplete,
  SelectOption,
  useMonthFilter,
  useNumberFilter,
} from "../SelectAutoComplete";
import { calculateAvailableDaysAndMonths } from "./calculateAvailableDaysAndMonths";
import { Month, Nil, toMonthShortName } from "cy-util";
import { FieldState } from "../models";

export interface DateOfBirthFormData {
  day: number | Nil;
  month: number | Nil;
  year: number | Nil;
  isValid: boolean;
}
export interface DateOfBirthProps {
  className?: string;
  minAge?: number;
  onChange: (value: DateOfBirthFormData | Nil) => void;
  value?: DateOfBirthFormData | Nil;
  fieldState?: FieldState;
  disabled?: boolean;
}

export const DateOfBirthPicker = forwardRef(DateOfBirthInner);
/**
 *
 */
function DateOfBirthInner(
  {
    className,
    minAge,
    value,
    onChange,
    fieldState,
    disabled,
  }: DateOfBirthProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  minAge = minAge || 18;
  const calculateDropdowns = useCallback(
    calculateAvailableDaysAndMonths(minAge),
    [minAge],
  );

  const [dayQ, setDayQ] = useState<string>();
  const [monthQ, setMonthQ] = useState<string>();
  const [yearQ, setYearQ] = useState<string>();
  const [currentDay, setCurrentDay] = useState<number | Nil>(
    value?.day || null,
  );
  const [currentMonth, setCurrentMonth] = useState<number | Nil>(
    value?.month || null,
  );
  const [currentYear, setCurrentYear] = useState<number | Nil>(
    value?.year || null,
  );
  const css = cx(className, "flex gap-2 date-of-birth-input");
  const { days, months, years } = calculateDropdowns(
    currentYear,
    currentMonth as Month,
  );

  const isValid = !!(currentDay && currentMonth && currentYear);

  useEffect(() => {
    if (!currentDay) return;
    if (!days.includes(currentDay)) {
      setCurrentDay(null);
    }
  }, [currentDay, days, setCurrentDay]);

  useEffect(() => {
    if (!currentMonth) return;
    if (!months.includes(currentMonth)) {
      setCurrentMonth(null);
    }
  }, [currentMonth, months, setCurrentMonth]);

  useEffect(() => {
    onChangeRef.current &&
      onChangeRef.current({
        day: currentDay,
        month: currentMonth,
        year: currentYear,
        isValid,
      });
  }, [currentDay, currentMonth, currentYear]);
  const filteredDays = useNumberFilter(days, dayQ);
  const filteredYears = useNumberFilter(years, yearQ);
  const filteredMonths = useMonthFilter(months, monthQ);

  return (
    <div className={css}>
      <SelectAutoComplete<number>
        disabled={disabled}
        name="dob-day"
        fieldState={fieldState}
        className={cx("date-of-brith-input__day w-20")}
        nullable
        ref={ref}
        items={filteredDays}
        value={currentDay}
        onQueryChange={setDayQ}
        onChange={setCurrentDay}
      >
        {({ item }) => {
          return (
            <SelectOption value={item} key={item}>
              {item}
            </SelectOption>
          );
        }}
      </SelectAutoComplete>
      <SelectAutoComplete
        name="dob-month"
        disabled={disabled}
        className={cx("date-of-brith-input__month w-20")}
        nullable
        fieldState={fieldState}
        items={filteredMonths}
        value={currentMonth}
        displayValue={(x) => (x ? toMonthShortName(x) : "")}
        onQueryChange={setMonthQ}
        onChange={setCurrentMonth}
      >
        {({ item }) => {
          return (
            <SelectOption value={item} key={item}>
              {item && toMonthShortName(item)}
            </SelectOption>
          );
        }}
      </SelectAutoComplete>
      <SelectAutoComplete
        name="dob-year"
        disabled={disabled}
        className={cx("date-of-brith-input__year w-24")}
        nullable
        fieldState={fieldState}
        items={filteredYears}
        value={currentYear}
        onQueryChange={setYearQ}
        onChange={setCurrentYear}
      >
        {({ item }) => {
          return (
            <SelectOption value={item} key={item}>
              {item}
            </SelectOption>
          );
        }}
      </SelectAutoComplete>
    </div>
  );
}
